<template>
  <div class="">
    <div class="flex justify-between flex-wrap items-center py-2">
      <div class="flex flex-wrap gap-4">
        <div class="w-1/5 min-w-[200px] pr-5 text-xs py-2">
          Search (ISP name)
          <v-text-field placeholder="Name" variant="outlined" density="compact" v-model="searchData" rounded="0" />
        </div>
        <div class="w-1/5 min-w-[200px] pr-5 text-xs py-2">
          Country
          <v-select v-model="selectedCountry" :items="countryOptions" density="compact" variant="outlined" rounded="0" />
        </div>
      </div>
      <div>
        <v-btn icon="mdi-delete" size="small" style="outline: none;" @click="deleteSelected"
          class="mr-5 bg-dark text-light"></v-btn>
        <v-btn icon="mdi-plus" size="small" style="outline: none;" class="bg-dark text-light" data-bs-toggle="modal"
          data-bs-target="#ispAddModal"></v-btn>
      </div>
    </div>

    <v-data-table :headers="headers" :items="filteredIsps" item-key="id"
      class="border h-[500px] bg-white text-black min-h-[500px]" items-per-page="-1" style="font-size: 13px;"
      :style="`height: ${computedHeight}px;`">
      <template v-slot:[`item.checkbox`]="{ item }">
        <div @click="toggleSelection(item)" class="ml-auto align-self-center"
          style="width: 20px; height: 20px; cursor: pointer;">
          <input :checked="isSelected(item)" class="form-check-input shadow-none" type="checkbox" />
        </div>
      </template>
      <template v-slot:[`item.isp_image`]="{ item }">
        <div class="w-full h-full flex items-center justify-center">
          <v-img v-if="item.isp_image" :src="item.isp_image" class="isp-img" contain></v-img>
          <span v-else class="text-gray-400 text-xs">-</span>
        </div>
      </template>
      <template v-slot:[`item.sellable`]="{ item }">
        <div class="w-full h-full flex justify-center items-center">
          <v-switch v-model="item.sellable" color="success" @click="toggleSellable(item)"></v-switch>
        </div>
      </template>
      <template v-slot:[`item.buttonBar`]="{ item }">
        <v-menu offset-y location="bottom">
          <template v-slot:activator="{ props, on, isActive }">
            <v-btn v-bind="props" v-on="{ ...on }" density="compact"
              :style="isActive ? 'border-bottom-left-radius: 0px;border-bottom-right-radius: 0px;' : ''"
              style="outline: none; font-size: 10px;" class="w-32 z-10 bg-dark text-light">
              Actions
              <v-icon icon="mdi-chevron-down" size="14"></v-icon>
            </v-btn>
          </template>
          <v-list class="w-32 flex-1 flex flex-col px-0 py-0 z-0"
            style="border-top-left-radius: 0px;border-top-right-radius: 0px">
            <v-btn v-for="(button, index) in actionButtons" :key="index" @click="clickAction(item, button)" rounded="0"
              class="text-center bg-dark text-light"
              style="font-size: 10px; width: 128px; outline: none; border-bottom: 1px solid #345;"
              :data-bs-toggle="button === 'Update' && 'modal'" :data-bs-target="button === 'Update' && '#ispUpdateModal'">
              {{ button }}
            </v-btn>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>
  </div>

  <AddNewOneDialog :countries="countries" @created="reload" />
  <UpdateInfoDialog :item="selectedItem" :countries="countries" @updated="reload" />
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { storeToRefs } from "pinia";
import { useAdminIspStore, useAdminCountryStore } from "@/stores";

import AddNewOneDialog from "./Modal/AddNewOneDialog.vue";
import UpdateInfoDialog from "./Modal/UpdateInfoDialog.vue";

const backendUrl = process.env.VUE_APP_VUE_SOCKET_URL;

const adminIspStore = useAdminIspStore();
const adminCountryStore = useAdminCountryStore();

const { isps } = storeToRefs(adminIspStore);
const { countries } = storeToRefs(adminCountryStore);

adminIspStore.loadIsps();
adminCountryStore.loadCountries();

const headers = ref([
  { title: "", align: "center", key: "checkbox" },
  { title: "ID", align: "center", key: "id" },
  { title: "ISP Name", align: "center", key: "name" },
  { title: "Country", align: "center", key: "country" },
  { title: "Image", align: "center", key: "isp_image" },
  { title: "Sellable", align: "center", key: "sellable" },
  { title: "Actions", align: "center", key: "buttonBar" },
]);

const searchData = ref("");
const selectedCountry = ref("All");
const selectedItems = ref([]);
const selectedItem = ref(null);
const actionButtons = ref(["Update", "Delete"]);

const offsetHeight = 300;
const computedHeight = ref(window.innerHeight - offsetHeight);
const handleResize = () => {
  computedHeight.value = window.innerHeight - offsetHeight;
};
onMounted(() => window.addEventListener("resize", handleResize));
onBeforeUnmount(() => window.removeEventListener("resize", handleResize));

const countryOptions = computed(() => ["All", ...(countries.value || []).map((c) => c.name)]);

const normalizedIsps = computed(() => {
  return (isps.value || []).map((i) => ({
    ...i,
    isp_image: i.isp_image ? `${backendUrl}/${i.isp_image}` : "",
  }));
});

const filteredIsps = computed(() => {
  const q = searchData.value.toLowerCase();
  return normalizedIsps.value.filter((i) => {
    const okName = String(i.name || "").toLowerCase().includes(q);
    const okCountry = selectedCountry.value === "All" || i.country === selectedCountry.value;
    return okName && okCountry;
  });
});

const isSelected = (item) => selectedItems.value.includes(item);
const toggleSelection = (item) => {
  const idx = selectedItems.value.indexOf(item);
  if (idx === -1) selectedItems.value.push(item);
  else selectedItems.value.splice(idx, 1);
};

const reload = async () => {
  selectedItems.value = [];
  selectedItem.value = null;
  await adminIspStore.loadIsps();
  await adminCountryStore.loadCountries();
};

const deleteSelected = async () => {
  if (!selectedItems.value.length) return;
  await adminIspStore.deleteIsps(selectedItems.value.map((i) => i.id));
  await reload();
};

const toggleSellable = async (item) => {
  await adminIspStore.updateIsp({ id: item.id, sellable: !item.sellable });
  await adminIspStore.loadIsps();
};

const clickAction = async (item, button) => {
  switch (button) {
    case "Update":
      selectedItem.value = item;
      break;
    case "Delete":
      await adminIspStore.deleteIsps([item.id]);
      await reload();
      break;
  }
};
</script>

<style scoped>
.isp-img {
  width: 90px;
  height: 56px;
}
:deep(.v-data-table .v-data-table__tr) {
  height: 72px;
}
:deep(.v-data-table .v-data-table__td) {
  padding-top: 8px;
  padding-bottom: 8px;
}
</style>

